import {
  ANNOTATION_RULES_UNLIMITED_GRANT,
  LIMIT_ANNOTATION_RULES,
  ANNOTATIONS_GRANT,
  AUDIT_HISTORY_GRANT,
  BACKFILL_GRANT,
  CUSTOM_INSTANCE_GRANT,
  GIVEAWAYS_GRANT,
  HISTORY_VIEW_UNLIMITED_GRANT,
  BRIEFING_WEEKS_UNLIMITED_GRANT,
  INSIGHTS_ALERTS_GRANT,
  INSIGHTS_FINDINGS_GRANT,
  INSIGHTS_INBOX_GRANT,
  LEVELING_ADVANCED_GRANT,
  LEVELING_GRANT,
  LEVELING_MEMBER_CARDS_GRANT,
  LEVELING_RECOUNT_GRANT,
  LEVELING_REWARD_CARDS_GRANT,
  LEVELING_RULES_UNLIMITED_GRANT,
  LIMIT_BRIEFING_ITEMS,
  LIMIT_BRIEFING_WEEKS,
  LIMIT_HISTORY_VIEW_DAYS,
  LIMIT_LEVELING_RULES,
  LIMIT_PERKS,
  LIMIT_PERK_CONDITIONS,
  PERKS_UNLIMITED_GRANT,
  LIMIT_SAVED_VIEWS,
  LIMIT_TEAM_POLICIES,
  TEAM_CUSTOM_POLICIES_GRANT,
  TEAM_POLICIES_UNLIMITED_GRANT,
  TEAM_ROLE_POLICIES_GRANT,
  PULSE_GRANT,
} from "@colossus/contracts";
import type { GrantOf } from "@colossus/contracts/module";
import type { Message } from "./locale.ts";
import { MODULE_TITLE } from "./modules.ts";
import type { Module } from "./modules.ts";
import { m } from "./paraglide/messages.js";

export type PlanRow = { readonly id: string; readonly label: Message } & (
  | { readonly kind: "flag"; readonly keys: readonly string[] }
  | { readonly kind: "held" }
  | { readonly kind: "request"; readonly key: string }
  | { readonly kind: "days"; readonly key: string; readonly unboundedBy: string }
  | { readonly kind: "count"; readonly key: string; readonly unboundedBy?: string }
  | { readonly kind: "images" }
  | {
      readonly kind: "tiered";
      readonly levels: readonly { readonly key: string | null; readonly label: Message }[];
    }
);

export interface PlanGroup {
  readonly id: string;
  readonly label: Message | null;
  readonly core: readonly PlanRow[];
  readonly rows: readonly PlanRow[];
  readonly held: readonly PlanRow[];
}

export interface PlanSection {
  readonly id: Module;
  readonly title: Message;
  readonly groups: readonly PlanGroup[];
}

const flagIn =
  <M extends Module>(_module: M) =>
  (id: string, label: Message, ...keys: GrantOf<M>[]): PlanRow => ({
    id,
    label,
    kind: "flag",
    keys,
  });

const intelligence = flagIn("intelligence");
const rewards = flagIn("rewards");
const workspace = flagIn("workspace");

const held = (id: string, label: Message): PlanRow => ({ id, label, kind: "held" });

const countIn =
  <M extends Module>(_module: M) =>
  (id: string, label: Message, key: GrantOf<M>, unboundedBy?: GrantOf<M>): PlanRow => ({
    id,
    label,
    kind: "count",
    key,
    ...(unboundedBy === undefined ? {} : { unboundedBy }),
  });

const intelligenceCount = countIn("intelligence");

const rewardsCount = countIn("rewards");

const workspaceCount = countIn("workspace");

const section = (id: Module, groups: readonly PlanGroup[]): PlanSection => ({
  id,
  title: MODULE_TITLE[id],
  groups,
});

const TEAM_ACCESS_ROWS: readonly PlanRow[] = [
  workspaceCount(
    "team-count",
    m.pricing_compare_team_count,
    LIMIT_TEAM_POLICIES,
    TEAM_POLICIES_UNLIMITED_GRANT,
  ),
  workspace("team-roles", m.pricing_compare_team_roles, TEAM_ROLE_POLICIES_GRANT),
  workspace("team-custom", m.pricing_compare_team_custom, TEAM_CUSTOM_POLICIES_GRANT),
  { id: "team-presets", label: m.pricing_compare_team_presets, kind: "held" },
];

const HISTORY_DAYS: GrantOf<"analytics"> = LIMIT_HISTORY_VIEW_DAYS;

const HISTORY_UNBOUNDED: GrantOf<"analytics"> = HISTORY_VIEW_UNLIMITED_GRANT;

const SAVED_VIEWS: GrantOf<"analytics"> = LIMIT_SAVED_VIEWS;

const GIVEAWAYS: GrantOf<"campaigns"> = GIVEAWAYS_GRANT;

export const PLAN_SECTIONS: readonly PlanSection[] = [
  section("analytics", [
    {
      id: "history",
      label: m.pricing_compare_group_history,
      core: [],
      rows: [
        {
          id: "retention",
          label: m.pricing_compare_retention,
          kind: "days",
          key: HISTORY_DAYS,
          unboundedBy: HISTORY_UNBOUNDED,
        },
      ],
      held: [],
    },
    {
      id: "dashboard",
      label: m.pricing_compare_group_dashboard,
      core: [],
      rows: [
        {
          id: "saved-views",
          label: m.pricing_compare_saved_views,
          kind: "count",
          key: SAVED_VIEWS,
        },
        held("full-screen", m.pricing_compare_full_screen),
      ],
      held: [],
    },
    {
      id: "every-plan",
      label: m.pricing_compare_group_every_plan,
      core: [],
      rows: [],
      held: [
        held("charts", m.pricing_compare_charts),
        held("profiles", m.pricing_feature_profiles),
        held("tools", m.pricing_compare_tools),
      ],
    },
  ]),
  section("intelligence", [
    {
      id: "insights",
      label: null,
      core: [],
      rows: [
        intelligence("pulse", m.pricing_compare_pulse, PULSE_GRANT),
        intelligence("findings", m.pricing_compare_findings, INSIGHTS_FINDINGS_GRANT),
        intelligenceCount("briefing", m.pricing_compare_briefing, LIMIT_BRIEFING_ITEMS),
        intelligenceCount(
          "briefing-history",
          m.pricing_compare_group_history,
          LIMIT_BRIEFING_WEEKS,
          BRIEFING_WEEKS_UNLIMITED_GRANT,
        ),
        intelligence("inbox", m.pricing_compare_inbox, INSIGHTS_INBOX_GRANT),
        intelligence("assign-snooze", m.pricing_compare_assign_snooze, INSIGHTS_INBOX_GRANT),
        {
          id: "delivery",
          label: m.pricing_compare_delivery,
          kind: "tiered",
          levels: [
            { key: null, label: m.pricing_delivery_channel },
            { key: INSIGHTS_INBOX_GRANT, label: m.pricing_delivery_dms },
            { key: INSIGHTS_ALERTS_GRANT, label: m.pricing_delivery_many },
          ],
        },
        {
          id: "alerts",
          label: m.pricing_compare_alerts,
          kind: "tiered",
          levels: [
            { key: null, label: m.insights_kind_join_burst },
            { key: INSIGHTS_ALERTS_GRANT, label: m.pricing_alerts_custom },
          ],
        },
        held("mute", m.pricing_compare_mute),
        intelligence("sensitivity", m.pricing_compare_sensitivity, INSIGHTS_ALERTS_GRANT),
        intelligence("annotations", m.pricing_compare_annotations, ANNOTATIONS_GRANT),
        intelligence(
          "annotation-impact",
          m.pricing_compare_annotation_impact,
          PULSE_GRANT,
          ANNOTATIONS_GRANT,
        ),
        intelligenceCount(
          "annotation-rules",
          m.pricing_compare_annotation_rules,
          LIMIT_ANNOTATION_RULES,
          ANNOTATION_RULES_UNLIMITED_GRANT,
        ),
      ],
      held: [],
    },
  ]),
  section("rewards", [
    {
      id: "levels",
      label: m.pricing_compare_group_levels,
      core: [
        rewards("core", m.pricing_compare_leveling_core, LEVELING_GRANT),
        rewards("import", m.pricing_compare_leveling_import, LEVELING_GRANT),
      ],
      rows: [
        rewardsCount(
          "rules",
          m.pricing_compare_leveling_rules,
          LIMIT_LEVELING_RULES,
          LEVELING_RULES_UNLIMITED_GRANT,
        ),
        rewards("curve", m.pricing_compare_leveling_curve, LEVELING_ADVANCED_GRANT),
        rewards("adjust-xp", m.pricing_compare_leveling_adjust_xp, LEVELING_ADVANCED_GRANT),
        rewards("recount", m.pricing_compare_leveling_recount, LEVELING_RECOUNT_GRANT),
      ],
      held: [],
    },
    {
      id: "rewards",
      label: m.leveling_page_roles,
      core: [],
      rows: [
        rewardsCount("perks", m.perks_allowance, LIMIT_PERKS, PERKS_UNLIMITED_GRANT),
        rewardsCount("perk-conditions", m.perks_allowance_conditions, LIMIT_PERK_CONDITIONS),
        rewards("managed-roles", m.pricing_compare_leveling_managed_roles, LEVELING_GRANT),
        rewards("personal-roles", m.features_personal_roles, LEVELING_ADVANCED_GRANT),
      ],
      held: [],
    },
    {
      id: "level-ups",
      label: m.pricing_compare_group_levelups,
      core: [rewards("announce", m.pricing_compare_leveling_announce, LEVELING_GRANT)],
      rows: [
        rewards("design", m.pricing_compare_leveling_design, LEVELING_ADVANCED_GRANT),
        { id: "images", label: m.pricing_compare_leveling_images, kind: "images" },
        rewards(
          "reward-cards",
          m.pricing_compare_leveling_reward_cards,
          LEVELING_REWARD_CARDS_GRANT,
        ),
        rewards(
          "member-cards",
          m.pricing_compare_leveling_member_cards,
          LEVELING_MEMBER_CARDS_GRANT,
        ),
      ],
      held: [],
    },
  ]),
  section("campaigns", [
    {
      id: "giveaways",
      label: null,
      core: [],
      rows: [{ id: "giveaways", label: m.nav_giveaways, kind: "request", key: GIVEAWAYS }],
      held: [],
    },
  ]),
  section("workspace", [
    {
      id: "workspace",
      label: null,
      core: [],
      rows: [
        ...TEAM_ACCESS_ROWS,
        workspace("audit", m.pricing_compare_audit, AUDIT_HISTORY_GRANT),
        workspace("own-bot", m.pricing_feature_own_bot, CUSTOM_INSTANCE_GRANT),
        workspace("imports", m.pricing_feature_imports, BACKFILL_GRANT),
      ],
      held: [held("privacy", m.pricing_compare_privacy)],
    },
  ]),
];
